import React from "react";
import type { ApolloClient } from "@apollo/client";
import IconTable, { type IconTableColumn } from "components/IconTable";
import Alert from "components/Alert";
import Button from "components/Button";
import StatusBox from "components/StatusBox";

import { Card } from "react-bootstrap";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import { toast } from "react-toastify";
import { flushErrors } from "actions/errorActions";
import { capitalize, isEmpty, map, size, some, transform } from "lodash";
import {
  approveEmployeeLinks,
  getAllEmployerGroups,
  rejectEmployeeLinks,
} from "actions/employerActions";
import type { EmployerGroup, EmployerLinkRequest } from "types/models";
import type { RootState } from "types/state";

const actions = [
  employerConstants.REJECT_PENDING_LINKED_EMPLOYEE,
  employerConstants.APPROVE_PENDING_LINKED_EMPLOYEE,
  employerConstants.GET_EMPLOYER_GROUPS,
];

const updatingSelector = createLoadingSelector(actions);

const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isUpdating: updatingSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  approveEmployeeLinks,
  rejectEmployeeLinks,
  getAllEmployerGroups,
  flushErrors,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  group: EmployerGroup;
  linkedEmployees: EmployerLinkRequest[];
  pendingLinkRequests: EmployerLinkRequest[];
  approvedPlans: Record<string, unknown>;
  rejectedPlans: Record<string, unknown>;
  planType: string;
  goToNamedStep: (stepName: string) => void;

  // Declared in the original propTypes but never read by this component.
  isApproving?: boolean;
  isRejecting?: boolean;
}

type Props = WithApolloClient<OwnProps> & PropsFromRedux;

interface PlanRow {
  id: string;
  employeeName: string;
  lastFourOfSsn?: string | null;
  email?: string | null;
  status?: string;
}

class ApproveEmployeeLinkRequest extends React.PureComponent<Props> {
  _getApprovalData = (): PlanRow[] => {
    const { existing, approved, rejected } = transform(
      [...this.props.linkedEmployees, ...this.props.pendingLinkRequests],
      (
        acc: { existing: PlanRow[]; approved: PlanRow[]; rejected: PlanRow[] },
        val: EmployerLinkRequest
      ) => {
        const {
          userProfile: { firstName, lastName, email, lastFourOfSsn },
        } = val as {
          userProfile: NonNullable<EmployerLinkRequest["userProfile"]>;
        };

        const fullName = `${firstName} ${lastName}`;
        const id = val.id;

        const isExisting = some(this.props.linkedEmployees, { id: val.id });
        const isNewApproved = !!this.props.approvedPlans[val.id];
        const isNewRejected = !!this.props.rejectedPlans[val.id];
        let status: string | undefined;
        const rowData: PlanRow = {
          id,
          employeeName: fullName,
          lastFourOfSsn,
          email,
          status,
        };
        if (isExisting || isNewApproved) {
          rowData.status = "Approved";
          isExisting ? acc.existing.push(rowData) : acc.approved.push(rowData);
        } else if (isNewRejected) {
          rowData.status = "Rejected";
          acc.rejected.push(rowData);
        }
      },
      {
        existing: [] as PlanRow[],
        approved: [] as PlanRow[],
        rejected: [] as PlanRow[],
      }
    );

    return [...rejected, ...approved, ...existing];
  };

  _buildPlanColumns = (): Array<IconTableColumn<PlanRow>> => {
    const columns: Array<IconTableColumn<PlanRow>> = [
      {
        label: "Employee Name",
        key: "employeeName",
      },

      {
        label: "Employee Email",
        key: "email",
      },
      {
        label: "Last 4 SSN",
        key: "lastFourOfSsn",
      },
      {
        customComponent: (props: PlanRow & Record<string, unknown>) => {
          let status;

          if (this.props.approvedPlans[props.id]) {
            status = <StatusBox status="Approved" type="success" />;
          } else if (this.props.rejectedPlans[props.id]) {
            status = <StatusBox status="Rejected" type="danger" />;
          } else {
            status = <StatusBox status="Existing" type="complete" />;
          }
          return <div>{status}</div>;
        },
      },
    ];
    return columns;
  };

  _submitPlanRequest = () => {
    const acceptedIds = map(this.props.approvedPlans, (val, id) => id);
    const rejectedIds = map(this.props.rejectedPlans, (val, id) => id);

    const promises = [];

    if (!isEmpty(this.props.approvedPlans)) {
      promises.push(
        this.props.approveEmployeeLinks(
          this.props.client as ApolloClient<object>,
          acceptedIds
        )
      );
    }

    if (!isEmpty(this.props.rejectedPlans)) {
      promises.push(
        this.props.rejectEmployeeLinks(
          this.props.client as ApolloClient<object>,
          rejectedIds
        )
      );
    }

    void Promise.all(promises).then((results) => {
      if (results.every((result) => result.success)) {
        toast.success("Successfully reviewed requests.");
        this.props.push("/dashboard/company/group");
      }
    });
  };

  render() {
    const columns = this._buildPlanColumns();
    const data = this._getApprovalData();

    const pending = this.props.pendingLinkRequests;
    const numberOfEmployees = size(pending);

    return (
      <>
        <Card>
          <div className="widget-header">
            <span>
              <Card.Title>
                {this.props.group.name}:
                <p className="invite-number">
                  Pending Employees (
                  <p className="number">{numberOfEmployees}</p>)
                </p>
              </Card.Title>
              <p className="detail">
                <span className="badge">
                  <span className="badge-style-plan-type">
                    <p className="type"> {capitalize(this.props.planType)}</p>
                  </span>
                </span>
              </p>
            </span>
          </div>

          <div className="plan-approval-table">
            {this.props.error && <Alert type="error" msg={this.props.error} />}
            <IconTable columns={columns} data={data} />
            <div className="close-btn">
              <Button
                onClick={() => this.props.goToNamedStep("existing")}
                btnLabel="Go Back"
                name="cancel"
                color="cancel"
              />
              <span style={{ paddingLeft: "10px" }}>
                <Button
                  btnLabel="Submit"
                  name="submit"
                  onClick={this._submitPlanRequest}
                  withArrow
                  loading={this.props.isUpdating}
                />
              </span>
            </div>
          </div>
        </Card>
      </>
    );
  }
}

export default connector(withApollo<Props>(ApproveEmployeeLinkRequest));
